import axios from 'axios'
import settings from '@/settings'

const CLUSTERS = [
  { id: 1, label: '50–100 км', min: 50, max: 100 },
  { id: 2, label: '101–500 км', min: 101, max: 500 },
  { id: 3, label: '501–1000 км', min: 501, max: 1000 },
  { id: 4, label: '1001–2000 км', min: 1001, max: 2000 },
  { id: 5, label: '2001+ км', min: 2001, max: Infinity },
]

const ACTIVITY_CATALOG = [
  { code: 'running', name: 'Бег', icon: 'fa-person-running' },
  { code: 'walking', name: 'Ходьба', icon: 'fa-person-walking' },
  { code: 'biking', name: 'Велосипед', icon: 'fa-person-biking' },
  { code: 'swimming', name: 'Плавание', icon: 'fa-person-swimming' },
]

function clubAuthHeaders () {
  return {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'SimpleRun-Club-Id': settings.CLUB_ID,
  }
}

function seasonGoalUrl () {
  return `${settings.APP_URL}custom-projects/clubs/${settings.CLUB_ID}/season/goal`
}

function clusterIdFor(km) {
  const n = Number(km)
  if (!Number.isFinite(n) || n < 50) return null
  const c = CLUSTERS.find(cl => n >= cl.min && n <= cl.max)
  return c ? c.id : null
}

function activityMetaFor(activityType) {
  const meta = ACTIVITY_CATALOG.find(a => a.code === activityType)
  if (meta) return meta
  return {
    code: activityType,
    name: String(activityType || '').replace(/_/g, ' '),
    icon: 'fa-dumbbell',
  }
}

function normalizeActivity(raw) {
  const activityType = raw?.activityType || raw?.code
  const meta = activityMetaFor(activityType)
  const goalKm = raw?.goalKm
  const hasGoal = goalKm != null && goalKm !== ''
  let clusterId = raw?.clusterId
  if ((clusterId == null || clusterId === '') && hasGoal) {
    clusterId = clusterIdFor(Number(goalKm))
  }
  return {
    code: meta.code,
    activityType: meta.code,
    name: meta.name,
    icon: meta.icon,
    goalKm: hasGoal ? Number(goalKm) : null,
    clusterId: clusterId != null && clusterId !== '' ? Number(clusterId) : null,
    passedKm: raw?.passedKm != null ? Number(raw.passedKm) : 0,
    savedAt: raw?.savedAt || null,
  }
}

function buildInitialActivities() {
  return ACTIVITY_CATALOG.map(a => normalizeActivity({ activityType: a.code }))
}

function normalizeActivitiesPayload(raw) {
  const rows = Array.isArray(raw) ? raw : raw ? [raw] : []
  const byType = rows.reduce((map, row) => {
    const activityType = row?.activityType || row?.code
    if (activityType) map[activityType] = row
    return map
  }, {})
  return ACTIVITY_CATALOG.map(a => normalizeActivity(byType[a.code] || { activityType: a.code }))
}

export default {
  namespaced: true,
  state: {
    activities: buildInitialActivities(),
    clustersRating: [],
    loading: {
      progress: false,
      rating: false,
      save: false,
    },
    title: '7‑й летний сезон «Будь здоров!»',
    startDate: '01.05.2026',
    endDate: '30.09.2026',
    endDateRaw: '2026-09-30',
    clusters: CLUSTERS,
    activityCatalog: ACTIVITY_CATALOG,
    diplomaThresholdPercent: 89,
    goalFetched: false,
    goalLoadError: false,
  },

  getters: {
    activities(state) {
      return state.activities
    },
    myActivities(state) {
      return state.activities.filter(a => a.goalKm && a.clusterId)
    },
    isGoalSet(state, getters) {
      return getters.myActivities.length > 0
    },
    myClustersByActivity(state, getters) {
      const map = {}
      getters.myActivities.forEach((a) => {
        map[a.code] = a.clusterId
      })
      return map
    },
    myClusterIds(state, getters) {
      return Array.from(new Set(getters.myActivities.map(a => a.clusterId)))
    },
    daysLeft(state) {
      const end = new Date(state.endDateRaw)
      const now = new Date()
      const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24))
      return diff > 0 ? diff : 0
    },
    showNoGoalBanner(state, getters) {
      return state.goalFetched && !state.goalLoadError && !getters.loading.progress && !getters.isGoalSet
    },
    goalLoadError(state) {
      return state.goalLoadError
    },
    clustersRating(state) {
      return state.clustersRating
    },
    seasonInfo(state) {
      return {
        title: state.title,
        startDate: state.startDate,
        endDate: state.endDate,
        clusters: state.clusters,
        activityCatalog: state.activityCatalog,
        diplomaThresholdPercent: state.diplomaThresholdPercent,
      }
    },
    clusterLabel: state => (id) => {
      const c = state.clusters.find(cl => cl.id === id)
      return c ? c.label : ''
    },
    loading(state) {
      return state.loading
    },
  },

  actions: {
    async getSeasonProgress({ commit }) {
      commit('START_PROGRESS_LOADING')
      try {
        const { data } = await axios.get(seasonGoalUrl(), {
          headers: clubAuthHeaders(),
        })
        commit('SET_PROGRESS', normalizeActivitiesPayload(data))
      } catch (e) {
        const status = e.response?.status
        if (status === 404 || status === 204) {
          commit('SET_PROGRESS', buildInitialActivities())
        } else {
          commit('SET_PROGRESS_ERROR')
        }
      } finally {
        commit('FINISH_PROGRESS_LOADING')
      }
    },
    async getClustersRating({ commit }) {
      commit('START_RATING_LOADING')
      // TODO: replace mock with axios.get(`${settings.APP_URL}clubs/${settings.CLUB_ID}/season/clusters-rating`)
      const mock = [
        {
          clusterId: 1,
          label: '50–100 км',
          members: [
            { place: 1, name: 'Орлов Виктор', goalKm: 100, progressKm: 96, percent: 96, isMe: false, inMyGroup: true, inMySubunit: true },
            { place: 2, name: 'Гаврилова Татьяна', goalKm: 80, progressKm: 74, percent: 93, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 3, name: 'Мельников Олег', goalKm: 100, progressKm: 88, percent: 88, isMe: false, inMyGroup: true, inMySubunit: false },
            { place: 4, name: 'Зайцева Екатерина', goalKm: 60, progressKm: 49, percent: 82, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 5, name: 'Журавлев Андрей', goalKm: 100, progressKm: 32, percent: 32, isMe: true, inMyGroup: true, inMySubunit: true },
            { place: 6, name: 'Федоров Константин', goalKm: 90, progressKm: 71, percent: 79, isMe: false, inMyGroup: true, inMySubunit: true },
            { place: 7, name: 'Крылова Алина', goalKm: 70, progressKm: 49, percent: 70, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 8, name: 'Сергеев Никита', goalKm: 100, progressKm: 62, percent: 62, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 9, name: 'Данилова Вера', goalKm: 50, progressKm: 28, percent: 56, isMe: false, inMyGroup: true, inMySubunit: false },
          ],
        },
        {
          clusterId: 2,
          label: '101–500 км',
          members: [
            { place: 1, name: 'Иванова Мария', goalKm: 400, progressKm: 387, percent: 97, isMe: false, inMyGroup: true, inMySubunit: true },
            { place: 2, name: 'Петров Алексей', goalKm: 350, progressKm: 327, percent: 93, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 3, name: 'Сидорова Анна', goalKm: 250, progressKm: 231, percent: 92, isMe: false, inMyGroup: true, inMySubunit: false },
            { place: 4, name: 'Козлов Дмитрий', goalKm: 200, progressKm: 182, percent: 91, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 5, name: 'Новикова Елена', goalKm: 300, progressKm: 265, percent: 88, isMe: false, inMyGroup: true, inMySubunit: true },
            { place: 6, name: 'Морозов Игорь', goalKm: 150, progressKm: 131, percent: 87, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 7, name: 'Волкова Светлана', goalKm: 450, progressKm: 384, percent: 85, isMe: false, inMyGroup: true, inMySubunit: false },
            { place: 8, name: 'Соколов Павел', goalKm: 200, progressKm: 168, percent: 84, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 9, name: 'Лебедева Ирина', goalKm: 300, progressKm: 248, percent: 83, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 10, name: 'Кузнецов Андрей', goalKm: 500, progressKm: 407, percent: 81, isMe: false, inMyGroup: true, inMySubunit: true },
            { place: 11, name: 'Смирнова Ольга', goalKm: 300, progressKm: 240, percent: 80, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 12, name: 'Попов Сергей', goalKm: 350, progressKm: 272, percent: 78, isMe: false, inMyGroup: true, inMySubunit: false },
            { place: 13, name: 'Журавлев Андрей', goalKm: 200, progressKm: 80, percent: 40, isMe: true, inMyGroup: true, inMySubunit: true },
            { place: 14, name: 'Тихонов Роман', goalKm: 200, progressKm: 72, percent: 36, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 15, name: 'Белова Юлия', goalKm: 250, progressKm: 85, percent: 34, isMe: false, inMyGroup: true, inMySubunit: false },
          ],
        },
        {
          clusterId: 3,
          label: '501–1000 км',
          members: [
            { place: 1, name: 'Громов Артем', goalKm: 900, progressKm: 841, percent: 93, isMe: false, inMyGroup: true, inMySubunit: true },
            { place: 2, name: 'Ромашова Ксения', goalKm: 800, progressKm: 728, percent: 91, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 3, name: 'Егоров Максим', goalKm: 700, progressKm: 623, percent: 89, isMe: false, inMyGroup: true, inMySubunit: false },
            { place: 4, name: 'Архипов Леонид', goalKm: 600, progressKm: 510, percent: 85, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 5, name: 'Тарасова Наталья', goalKm: 1000, progressKm: 820, percent: 82, isMe: false, inMyGroup: true, inMySubunit: true },
            { place: 6, name: 'Степанов Руслан', goalKm: 550, progressKm: 440, percent: 80, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 7, name: 'Антонова Жанна', goalKm: 750, progressKm: 585, percent: 78, isMe: false, inMyGroup: true, inMySubunit: false },
            { place: 8, name: 'Дорохов Илья', goalKm: 900, progressKm: 666, percent: 74, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 9, name: 'Павлова Лариса', goalKm: 600, progressKm: 414, percent: 69, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 10, name: 'Комаров Евгений', goalKm: 850, progressKm: 493, percent: 58, isMe: false, inMyGroup: true, inMySubunit: true },
          ],
        },
        {
          clusterId: 4,
          label: '1001–2000 км',
          members: [
            { place: 1, name: 'Шестаков Борис', goalKm: 1500, progressKm: 1420, percent: 95, isMe: false, inMyGroup: true, inMySubunit: true },
            { place: 2, name: 'Киселева Анастасия', goalKm: 1800, progressKm: 1674, percent: 93, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 3, name: 'Воронов Тимофей', goalKm: 2000, progressKm: 1780, percent: 89, isMe: false, inMyGroup: true, inMySubunit: false },
            { place: 4, name: 'Фомина Полина', goalKm: 1200, progressKm: 1044, percent: 87, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 5, name: 'Беляев Глеб', goalKm: 1600, progressKm: 1328, percent: 83, isMe: false, inMyGroup: true, inMySubunit: true },
            { place: 6, name: 'Корнеева Галина', goalKm: 1100, progressKm: 858, percent: 78, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 7, name: 'Назаров Степан', goalKm: 1400, progressKm: 1036, percent: 74, isMe: false, inMyGroup: true, inMySubunit: false },
          ],
        },
        {
          clusterId: 5,
          label: '2001+ км',
          members: [
            { place: 1, name: 'Голубев Николай', goalKm: 3000, progressKm: 2790, percent: 93, isMe: false, inMyGroup: true, inMySubunit: true },
            { place: 2, name: 'Миронова Валентина', goalKm: 2500, progressKm: 2225, percent: 89, isMe: false, inMyGroup: false, inMySubunit: false },
            { place: 3, name: 'Филатов Георгий', goalKm: 2200, progressKm: 1870, percent: 85, isMe: false, inMyGroup: true, inMySubunit: false },
            { place: 4, name: 'Рязанцев Матвей', goalKm: 2800, progressKm: 2296, percent: 82, isMe: false, inMyGroup: false, inMySubunit: false },
          ],
        },
      ]
      await Promise.resolve(mock)
      commit('SET_CLUSTERS_RATING', mock)
      commit('FINISH_RATING_LOADING')
    },
    async saveGoals({ commit }, { activities }) {
      commit('START_SAVE_LOADING')
      try {
        const payload = activities.map(a => {
          const goalKm = Number(a.goalKm)
          return {
            goalKm,
            clusterId: clusterIdFor(goalKm),
            activityType: a.activityType || a.code,
          }
        })
        const { data } = await axios.post(seasonGoalUrl(), payload, {
          headers: clubAuthHeaders(),
        })
        commit('SET_GOALS', normalizeActivitiesPayload(data))
      } finally {
        commit('FINISH_SAVE_LOADING')
      }
    },
    clear({ commit }) {
      commit('clear')
    },
  },

  mutations: {
    SET_PROGRESS(state, activities) {
      state.activities = activities
      state.goalLoadError = false
    },
    SET_PROGRESS_ERROR(state) {
      state.goalLoadError = true
    },
    SET_CLUSTERS_RATING(state, rating) {
      state.clustersRating = rating
    },
    SET_GOALS(state, activities) {
      state.activities = activities
      state.goalLoadError = false
    },
    START_PROGRESS_LOADING(state) {
      state.loading.progress = true
      state.goalLoadError = false
    },
    FINISH_PROGRESS_LOADING(state) {
      state.loading.progress = false
      state.goalFetched = true
    },
    START_RATING_LOADING(state) {
      state.loading.rating = true
    },
    FINISH_RATING_LOADING(state) {
      state.loading.rating = false
    },
    START_SAVE_LOADING(state) {
      state.loading.save = true
    },
    FINISH_SAVE_LOADING(state) {
      state.loading.save = false
    },
    clear(state) {
      state.clustersRating = []
      state.loading = {
        progress: false,
        rating: false,
        save: false,
      }
      state.goalLoadError = false
    },
  },
}
